import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Alert, AlertDescription } from './ui/alert';
import { FileUp, CheckCircle, XCircle, Clock, Eye, Bell, Shield, Loader2, CreditCard } from 'lucide-react';
import type { User } from '../App';
import type { InnovationSubmission } from '../api/innovations';
import { listSubmissions } from '../api/innovations';
import { requestFilingDocumentationPrep } from '../api/innovations';

interface InnovatorDashboardProps {
  user: User;
  onSubmitClick: () => void;
  onReviewClick: (id: string) => void;
  onPayFilingDocBilling?: () => void;
}

export function InnovatorDashboard({ user, onSubmitClick, onReviewClick, onPayFilingDocBilling }: InnovatorDashboardProps) {
  const [submissions, setSubmissions] = useState<InnovationSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInnovationForFiling, setSelectedInnovationForFiling] = useState<InnovationSubmission | null>(null);
  const [filingDocRequestLoading, setFilingDocRequestLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listSubmissions(1, 100);
        if (!cancelled) {
          const list = res.submissions || [];
          const myId = user?.id;
          const onlyMine =
            myId && list.some((s) => s.submitter_uuid != null)
              ? list.filter((s) => s.submitter_uuid === myId)
              : list;
          setSubmissions(onlyMine);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load your submissions');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const pendingCount = submissions.filter(s => s.status === 'submitted' || s.status === 'under_review').length;
  const completedCount = submissions.filter(s => s.status === 'completed').length;
  const draftCount = submissions.filter(s => s.status === 'draft').length;
  const rejectedCount = submissions.filter(s => s.status === 'rejected').length;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge variant="outline" className="text-slate-600 border-slate-400">Draft</Badge>;
      case 'submitted':
      case 'under_review':
        return <Badge variant="outline" className="text-amber-600 border-amber-600"><Clock className="w-3 h-3 mr-1" />Pending Assessment</Badge>;
      case 'completed':
        return <Badge className="bg-[var(--royal)] hover:opacity-90 text-white"><CheckCircle className="w-3 h-3 mr-1" />Assessment Completed</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 border-red-300"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleRequestFilingDocumentation = async () => {
    if (!selectedInnovationForFiling) return;
    setFilingDocRequestLoading(true);
    try {
      const res = await requestFilingDocumentationPrep(selectedInnovationForFiling.submission_uuid);
      const now = new Date().toISOString();
      setSubmissions((prev) =>
        prev.map((s) =>
          s.submission_uuid === selectedInnovationForFiling.submission_uuid
            ? {
                ...s,
                filing_documentation_prep: {
                  ...(s.filing_documentation_prep || {}),
                  requested_at: now,
                },
              }
            : s
        )
      );
      alert(res.message || `Request recorded for "${selectedInnovationForFiling.title}".`);
      setSelectedInnovationForFiling(null);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed to submit request';
      alert(msg);
    } finally {
      setFilingDocRequestLoading(false);
    }
  };

  const getProgressTag = (sub: InnovationSubmission) => {
    const prep = sub.filing_documentation_prep;
    const isInstitutionSubmission = !!sub.institution_uuid;
    if (sub.status === 'submitted') {
      return <Badge variant="outline" className="text-sky-700 border-sky-400">In reviewer assignment pool</Badge>;
    }
    if (sub.status === 'under_review' && sub.feedback_approval_status !== 'pending_superadmin_review') {
      return <Badge variant="outline" className="text-amber-700 border-amber-400">Under reviewer assessment</Badge>;
    }
    if (prep?.package_ready_at && prep.package_url) {
      return <Badge className="bg-emerald-700 text-white">Filing documentation ready</Badge>;
    }
    if (prep?.requested_at && !prep.package_ready_at) {
      return <Badge className="bg-indigo-600 text-white">Preparing filing documentation</Badge>;
    }
    if (sub.filing_request_status === 'in_support') {
      return <Badge className="bg-indigo-600 text-white">Reviewer Supporting Filing</Badge>;
    }
    if (sub.filing_request_status === 'ready_to_file') {
      return <Badge className="bg-emerald-600 text-white">Ready to File</Badge>;
    }
    if (sub.filing_request_status === 'closed') {
      return <Badge variant="outline" className="text-slate-700 border-slate-400">Filing Request Closed</Badge>;
    }
    if (sub.feedback_approval_status === 'pending_superadmin_review') {
      return <Badge variant="outline" className="text-amber-700 border-amber-400">Awaiting Superadmin Approval</Badge>;
    }
    if (sub.feedback_approval_status === 'changes_requested') {
      return <Badge variant="outline" className="text-rose-700 border-rose-400">Reviewer Updating Feedback</Badge>;
    }
    if (
      sub.feedback_approval_status === 'approved' &&
      !prep?.requested_at &&
      !sub.filing_request_notified_to_reviewer
    ) {
      if (
        !isInstitutionSubmission &&
        !prep?.billing_cleared_at &&
        !prep?.payment_confirmed_at
      ) {
        return (
          <Badge className="bg-amber-700 text-white border-amber-800">
            Awaiting filing documentation billing (Insolip)
          </Badge>
        );
      }
      return <Badge className="bg-blue-600 text-white">Action: Request filing documentation</Badge>;
    }
    if (sub.filing_request_notified_to_reviewer) {
      return <Badge className="bg-blue-600 text-white">Reviewer Notified for Filing</Badge>;
    }
    return <Badge variant="secondary">No New Updates</Badge>;
  };

  const formatDate = (d?: string) => (d ? new Date(d).toLocaleDateString() : '—');

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {completedCount > 0 && (
        <Alert className="bg-[var(--gold-pale)] border-[rgba(201,168,76,0.3)]">
          <Bell className="h-4 w-4 text-[var(--gold)]" />
          <AlertDescription className="text-[var(--navy)]">
            <strong>Assessment Complete!</strong> You have {completedCount} submission{completedCount !== 1 ? 's' : ''} that {completedCount === 1 ? 'has' : 'have'} been assessed. View details below.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[var(--navy)] font-display font-semibold mb-2">
            {user.role === 'institution' ? 'Institution' : 'Innovator'} Dashboard
          </h1>
          <p className="text-[var(--navy)]/70">
            Manage your innovation submissions and track assessment progress
          </p>
        </div>
        <Button onClick={onSubmitClick}>
          <FileUp className="w-4 h-4 mr-2" />
          Submit Innovation
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-[rgba(201,168,76,0.15)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[var(--navy)] font-display">Draft</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-[var(--navy)] font-semibold">{draftCount}</div>
            <p className="text-[var(--navy)]/60">Not yet submitted</p>
          </CardContent>
        </Card>
        <Card className="border-[rgba(201,168,76,0.15)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[var(--navy)] font-display">Pending Assessment</CardTitle>
            <Clock className="w-5 h-5 text-[var(--gold)]" />
          </CardHeader>
          <CardContent>
            <div className="text-[var(--navy)] font-semibold">{pendingCount}</div>
            <p className="text-[var(--navy)]/60">Awaiting evaluation</p>
          </CardContent>
        </Card>
        <Card className="border-[rgba(201,168,76,0.15)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[var(--navy)] font-display">Assessment Completed</CardTitle>
            <CheckCircle className="w-5 h-5 text-[var(--royal)]" />
          </CardHeader>
          <CardContent>
            <div className="text-[var(--navy)] font-semibold">{completedCount}</div>
            <p className="text-[var(--navy)]/60">Evaluations received</p>
          </CardContent>
        </Card>
        <Card className="border-[rgba(201,168,76,0.15)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[var(--navy)] font-display">Rejected</CardTitle>
            <XCircle className="w-5 h-5 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-[var(--navy)] font-semibold">{rejectedCount}</div>
            <p className="text-[var(--navy)]/60">Not accepted</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-[rgba(201,168,76,0.15)]">
        <CardHeader>
          <CardTitle className="text-[var(--navy)] font-display">Your submissions</CardTitle>
          <CardDescription className="text-[var(--navy)]/70">Only your own innovation submissions are shown here</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
            </div>
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Submission Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Updates</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-[var(--navy)]/70 py-8">
                    No submissions yet. Click &quot;Submit Innovation&quot; to get started.
                  </TableCell>
                </TableRow>
              ) : (
                submissions.map((sub) => (
                  <TableRow key={sub.submission_uuid}>
                    <TableCell className="text-slate-900 font-medium">{sub.title}</TableCell>
                    <TableCell className="text-slate-600">{sub.current_state}</TableCell>
                    <TableCell className="text-slate-600">{formatDate(sub.submission_date)}</TableCell>
                    <TableCell>{getStatusBadge(sub.status)}</TableCell>
                    <TableCell>{getProgressTag(sub)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onReviewClick(sub.submission_uuid)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        {sub.status === 'completed' && sub.feedback_approval_status === 'approved' && (
                          <>
                            {user.role === 'innovator' &&
                              !sub.institution_uuid &&
                              !sub.filing_documentation_prep?.billing_cleared_at &&
                              !sub.filing_documentation_prep?.payment_confirmed_at &&
                              onPayFilingDocBilling && (
                                <Button variant="secondary" size="sm" onClick={onPayFilingDocBilling}>
                                  <CreditCard className="w-4 h-4 mr-1" />
                                  Pay filing fee (Paystack)
                                </Button>
                              )}
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => setSelectedInnovationForFiling(sub)}
                            >
                              <Shield className="w-4 h-4 mr-1" />
                              Filing documentation
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>

      {/* Filing Modal */}
      {selectedInnovationForFiling && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto border-slate-200 bg-white text-slate-900 shadow-2xl">
            <CardHeader className="border-b border-slate-200 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Filing documentation preparation</CardTitle>
                  <CardDescription>{selectedInnovationForFiling.title}</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedInnovationForFiling(null)}>
                  ✕
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="pt-6 space-y-4">
              {selectedInnovationForFiling.filing_documentation_prep?.package_url && (
                <Alert className="border-emerald-300 bg-emerald-50">
                  <AlertDescription className="text-emerald-950">
                    <strong>Your filing documentation package is ready.</strong>{' '}
                    <a
                      href={selectedInnovationForFiling.filing_documentation_prep.package_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--royal)] font-medium underline break-all"
                    >
                      Open documentation link
                    </a>
                  </AlertDescription>
                </Alert>
              )}
              {selectedInnovationForFiling.recommended_ip_types &&
                selectedInnovationForFiling.recommended_ip_types.length > 0 && (
                  <Alert className="bg-[var(--gold-pale)] border-[rgba(201,168,76,0.3)]">
                    <Shield className="h-4 w-4 text-[var(--gold)]" />
                    <AlertDescription className="text-[var(--navy)]">
                      <strong>Reviewer recommendations (for your own filing choices):</strong>{' '}
                      {selectedInnovationForFiling.recommended_ip_types.join(', ')}. Insolip does not file on your behalf;
                      we prepare filing documentation for you to use with your chosen office or agent.
                    </AlertDescription>
                  </Alert>
                )}

              {selectedInnovationForFiling.institution_uuid ? (
                <Alert>
                  <AlertDescription className="text-slate-800 space-y-2">
                    <p>
                      <strong>Institution workflow.</strong> Commercial terms for filing documentation are agreed{' '}
                      <strong>outside this application</strong>. After you submit the request here, a super administrator
                      prepares documentation for the agreed innovations and publishes a secure link when it is ready.
                    </p>
                    <p>You remain responsible for any official filing with the relevant Intellectual Property office.</p>
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert>
                  <AlertDescription className="text-slate-800 space-y-2">
                    <p>
                      <strong>Individual innovator.</strong> Insolip records in this application when you have been{' '}
                      <strong>billed</strong> for filing-documentation preparation; only then can you submit the request
                      below. Payment is not processed inside the app.
                    </p>
                    <p>
                      A <strong>super administrator</strong> prepares the filing documentation and publishes a secure link
                      here when it is ready. You remain responsible for any official filing with the relevant Intellectual
                      Property office.
                    </p>
                  </AlertDescription>
                </Alert>
              )}
              {!selectedInnovationForFiling.institution_uuid &&
                !selectedInnovationForFiling.filing_documentation_prep?.billing_cleared_at &&
                !selectedInnovationForFiling.filing_documentation_prep?.payment_confirmed_at &&
                !selectedInnovationForFiling.filing_documentation_prep?.requested_at && (
                  <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-3">
                    You cannot submit a request yet. Once Insolip has recorded billing for this submission, the request
                    button will be enabled.
                  </p>
                )}

              {selectedInnovationForFiling.filing_documentation_prep?.requested_at ? (
                <p className="text-sm text-slate-700">
                  You have already requested filing documentation preparation for this submission. Track status in the
                  table &quot;Updates&quot; column.
                </p>
              ) : null}

              <div className="flex gap-3 pt-4 border-t border-slate-200 sticky bottom-0 bg-white">
                <Button variant="outline" className="flex-1" onClick={() => setSelectedInnovationForFiling(null)}>
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleRequestFilingDocumentation}
                  disabled={
                    !!selectedInnovationForFiling.filing_documentation_prep?.requested_at ||
                    filingDocRequestLoading ||
                    (!selectedInnovationForFiling.institution_uuid &&
                      !selectedInnovationForFiling.filing_documentation_prep?.billing_cleared_at &&
                      !selectedInnovationForFiling.filing_documentation_prep?.payment_confirmed_at)
                  }
                >
                  {filingDocRequestLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Shield className="w-4 h-4 mr-2" />
                  )}
                  {filingDocRequestLoading ? 'Submitting…' : 'Request documentation preparation'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
